import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../../../src/components/DashboardLayout/DasboardLayout";
import { API_URL } from "../../../src/components/config";
import { Trophy, TrendingUp, UserCheck, RefreshCw, Download } from "lucide-react";

const TopTwentyDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareTrips, setRideshareTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("tripCount");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [driversRes, tripsRes, rideshareRequestsRes, rideshareTripsRes] = await Promise.all([
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`)
      ]);

      const [driversData, tripsData, rideshareRequestsData, rideshareTripsData] = await Promise.all([
        driversRes.json(),
        tripsRes.json(),
        rideshareRequestsRes.json(),
        rideshareTripsRes.json()
      ]);

      setDrivers(driversData || []);
      setTrips(tripsData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareTrips(Array.isArray(rideshareTripsData) ? rideshareTripsData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const topDriversData = useMemo(() => {
    // Rideshare routes owned by each driver (rideshare_trips.driver_id), so we can then find
    // every accepted passenger request (rideshare_requests.rideshare_id) that belongs to them.
    const rideshareIdsByDriverId = {};
    rideshareTrips.forEach(rTrip => {
      if (!rideshareIdsByDriverId[rTrip.driver_id]) {
        rideshareIdsByDriverId[rTrip.driver_id] = new Set();
      }
      rideshareIdsByDriverId[rTrip.driver_id].add(rTrip.rideshare_id);
    });

    const driversWithMetrics = drivers.map(driver => {
      const driverTrips = trips.filter(trip => trip.driver_id === driver.driver_id);
      const driverRideshareIds = rideshareIdsByDriverId[driver.driver_id] || new Set();
      const driverRideshareRequests = rideshareRequests.filter(req =>
        driverRideshareIds.has(req.rideshare_id)
      );

      const privateValue = driverTrips.reduce((sum, trip) =>
        sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0
      );
      const rideshareValue = driverRideshareRequests.reduce((sum, req) =>
        sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0
      );

      const tripCount = driverTrips.length + driverRideshareRequests.length;
      const totalValue = privateValue + rideshareValue;
      const avgValue = tripCount > 0 ? totalValue / tripCount : 0;

      return {
        ...driver,
        tripCount,
        totalValue,
        avgValue
      };
    });

    return driversWithMetrics
      .filter(d => d.tripCount > 0)
      .sort((a, b) => {
        switch(sortBy) {
          case "tripCount":
            return b.tripCount - a.tripCount;
          case "totalValue":
            return b.totalValue - a.totalValue;
          case "avgValue":
            return b.avgValue - a.avgValue;
          default:
            return b.tripCount - a.tripCount;
        }
      })
      .slice(0, 20);
  }, [drivers, trips, rideshareRequests, rideshareTrips, sortBy]);

  const exportData = () => {
    const csv = [
      ['Rank', 'Driver ID', 'Name', 'Email', 'Phone', 'Vehicle', 'Trips Completed', 'Total Revenue', 'Avg Revenue'],
      ...topDriversData.map((driver, index) => [
        index + 1,
        driver.driver_id,
        `${driver.first_name} ${driver.last_name}`,
        driver.email,
        driver.phone,
        `${driver.vehicle_make} ${driver.vehicle_model}`,
        driver.tripCount,
        driver.totalValue.toFixed(2),
        driver.avgValue.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'top_20_drivers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Trophy className="text-yellow-500" size={32} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Top 20 Performing Drivers
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Highest performing drivers by {sortBy === 'tripCount' ? 'trip count' : sortBy === 'totalValue' ? 'total revenue' : 'average revenue'}
              </p>
            </div>
          </div>
          <button
            onClick={exportData}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download size={16} />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Sort Options */}
        <div className="flex space-x-2 mb-6">
          {[
            { value: "tripCount", label: "Trip Count", icon: UserCheck },
            { value: "totalValue", label: "Total Revenue", icon: TrendingUp },
            { value: "avgValue", label: "Avg Revenue", icon: TrendingUp }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                sortBy === option.value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              <option.icon size={16} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Trips</p>
                <p className="text-2xl font-bold">
                  {topDriversData.reduce((sum, d) => sum + d.tripCount, 0)}
                </p>
              </div>
              <UserCheck size={24} className="opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Revenue</p>
                <p className="text-2xl font-bold">
                  ${topDriversData.reduce((sum, d) => sum + d.totalValue, 0).toFixed(0)}
                </p>
              </div>
              <TrendingUp size={24} className="opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Avg Performance</p>
                <p className="text-2xl font-bold">
                  {(topDriversData.reduce((sum, d) => sum + d.tripCount, 0) / topDriversData.length).toFixed(1)}
                </p>
              </div>
              <Trophy size={24} className="opacity-80" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-6 py-4">Rank</th>
                <th className="px-6 py-4">Driver</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Vehicle</th>
                <th className="px-6 py-4">Performance</th>
                <th className="px-6 py-4">Revenue</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {topDriversData.map((driver, index) => (
                <tr 
                  key={driver.driver_id} 
                  className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    index < 3 ? 'bg-gradient-to-r from-yellow-50 to-transparent dark:from-yellow-900/20' : ''
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-bold text-gray-500">#{index + 1}</span>
                      {index === 0 && <span className="text-2xl">🥇</span>}
                      {index === 1 && <span className="text-2xl">🥈</span>}
                      {index === 2 && <span className="text-2xl">🥉</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {driver.first_name} {driver.last_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {driver.driver_id}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-xs">{driver.email}</p>
                      <p className="text-xs">{driver.phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs">
                      <p className="font-medium">{driver.vehicle_make} {driver.vehicle_model}</p>
                      <p className="text-gray-500">{driver.vehicle_type}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-600">{driver.tripCount}</p>
                      <p className="text-xs text-gray-500">trips</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-green-600">
                        ${driver.totalValue.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">
                        avg: ${driver.avgValue.toFixed(2)}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      driver.status === 'Active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                    }`}>
                      {driver.status || 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {topDriversData.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500 dark:text-gray-400">
              No driver performance data available
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TopTwentyDrivers;
